import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_ADMIN_API || 'http://127.0.0.1:8003';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// ===================== AUTH HELPERS =====================

const getToken = () => localStorage.getItem('admin_token');
const setToken = (token) => localStorage.setItem('admin_token', token);
const clearToken = () => localStorage.removeItem('admin_token');

const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  return fetch(url, { ...options, headers });
};

// ===================== LOGIN SCREEN =====================

const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Login mislukt');
      }
      
      setToken(data.token);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🔐</div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Betaaldsolliciteren.nl</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gebruikersnaam
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="admin"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Wachtwoord
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Inloggen...' : 'Inloggen'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ===================== STYLED COMPONENTS =====================

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${className}`}>
    {children}
  </div>
);

const StatCard = ({ title, value, subtitle, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  
  return (
    <Card className={`p-4 ${colors[color]}`}>
      <div className="text-sm font-medium opacity-75">{title}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      {subtitle && <div className="text-xs mt-1 opacity-60">{subtitle}</div>}
    </Card>
  );
};

const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    error: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

const Button = ({ children, onClick, variant = 'primary', size = 'md', disabled = false }) => {
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    success: 'bg-green-600 text-white hover:bg-green-700',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  };
  
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md font-medium transition-colors ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

// ===================== TAB COMPONENTS =====================

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-medium border-b-2 transition-colors ${
      active 
        ? 'border-blue-600 text-blue-600' 
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
);

// ===================== TABLE COMPONENTS =====================

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200">
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {children}
      </tbody>
    </table>
  </div>
);

// ===================== DETAIL MODAL =====================

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN ADMIN DASHBOARD =====================

export default function AdminDashboard() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [matches, setMatches] = useState([]);
  const [simulatedCompanies, setSimulatedCompanies] = useState([]);
  const [rematchingIds, setRematchingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Detail modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); // 'candidate', 'company', 'match'
  const [modalData, setModalData] = useState(null);
  const [exportingScorecard, setExportingScorecard] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editJson, setEditJson] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  
  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importType, setImportType] = useState('candidate'); // 'candidate' or 'company'
  const [importJson, setImportJson] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  
  // Fetch initial data
  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setCheckingAuth(false);
        return;
      }
      
      try {
        const res = await fetch(`${API_BASE}/api/admin/verify`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (data.valid) {
          setIsAuthenticated(true);
          fetchData();
        } else {
          clearToken();
        }
      } catch {
        clearToken();
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, []);
  
  const handleLogin = () => {
    setIsAuthenticated(true);
    fetchData();
  };
  
  const handleLogout = async () => {
    try {
      await authFetch(`${API_BASE}/api/admin/logout`, { method: 'POST' });
    } catch {}
    clearToken();
    setIsAuthenticated(false);
  };
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statsRes, candRes, compRes, matchRes, simCompRes] = await Promise.all([
        authFetch(`${API_BASE}/api/admin/stats`),
        authFetch(`${API_BASE}/api/admin/candidates`),
        authFetch(`${API_BASE}/api/admin/companies`),
        authFetch(`${API_BASE}/api/admin/matches`),
        authFetch(`${API_BASE}/api/admin/simulated-companies`),
      ]);
      
      // Check for auth errors
      if (statsRes.status === 401) {
        clearToken();
        setIsAuthenticated(false);
        return;
      }
      
      if (!statsRes.ok || !candRes.ok || !compRes.ok || !matchRes.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const [statsData, candData, compData, matchData, simCompData] = await Promise.all([
        statsRes.json(),
        candRes.json(),
        compRes.json(),
        matchRes.json(),
        simCompRes.ok ? simCompRes.json() : { simulated_companies: [] },
      ]);
      
      setStats(statsData);
      setCandidates(candData.candidates || []);
      setCompanies(compData.companies || []);
      setMatches(matchData.matches || []);
      setSimulatedCompanies(simCompData.simulated_companies || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // View detail handlers
  const viewCandidateDetail = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/candidate/${id}`);
      const data = await res.json();
      setModalType('candidate');
      setModalData(data);
      setModalOpen(true);
    } catch (err) {
      alert('Kon details niet laden');
    }
  };
  
  const viewCompanyDetail = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/company/${id}`);
      const data = await res.json();
      setModalType('company');
      setModalData(data);
      setModalOpen(true);
    } catch (err) {
      alert('Kon details niet laden');
    }
  };
  
  const viewMatchDetail = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/match/${id}`);
      const data = await res.json();
      setModalType('match');
      setModalData(data);
      setModalOpen(true);
    } catch (err) {
      alert('Kon details niet laden');
    }
  };
  
  // Export handlers - need auth token in URL or use different approach
  const downloadJSON = async (url) => {
    // For authenticated downloads, we fetch and create a blob
    try {
      const res = await authFetch(`${API_BASE}${url}`);
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = url.split('/').pop() + '.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
    } catch (err) {
      alert('Download mislukt');
    }
  };
  
  // Trigger handlers
  const triggerMatching = async () => {
    if (!window.confirm('Wil je de batch matching starten?')) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/trigger/matching`, { method: 'POST' });
      const data = await res.json();
      alert(data.message || 'Matching gestart');
    } catch (err) {
      alert('Fout bij starten matching');
    }
  };
  
  const triggerNotifications = async () => {
    if (!window.confirm('Wil je de email notificaties versturen?')) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/trigger/notifications`, { method: 'POST' });
      const data = await res.json();
      alert(data.message || 'Notificaties gestart');
    } catch (err) {
      alert('Fout bij starten notificaties');
    }
  };
  
  // Delete handlers
  const deleteCandidate = async (id, name) => {
    if (!window.confirm(`Weet je zeker dat je kandidaat "${name}" (${id}) wilt verwijderen?\n\nDit verwijdert ook alle matches en kan niet ongedaan gemaakt worden!`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/candidate/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verwijderen mislukt');
      alert(data.message || 'Kandidaat verwijderd');
      fetchData(); // Refresh data
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };
  
  const deleteCompany = async (id, name) => {
    if (!window.confirm(`Weet je zeker dat je bedrijf "${name}" (${id}) wilt verwijderen?\n\nDit verwijdert ook alle matches en kan niet ongedaan gemaakt worden!`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/company/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verwijderen mislukt');
      alert(data.message || 'Bedrijf verwijderd');
      fetchData();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };
  
  const deleteMatch = async (id) => {
    if (!window.confirm(`Weet je zeker dat je match #${id} wilt verwijderen?`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/match/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verwijderen mislukt');
      alert(data.message || 'Match verwijderd');
      fetchData();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };

  const rematchSingle = async (matchId, candidateName, companyName) => {
    if (!window.confirm(`Deze match opnieuw berekenen voor ${candidateName} × ${companyName}?\n\nDit overschrijft de huidige analyse.`)) return;

    setRematchingIds(prev => [...prev, matchId]);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/match/${matchId}/rematch`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rematch mislukt');

      const oldScore = typeof data.old_score === 'number' ? data.old_score.toFixed(1) : '-';
      const newScore = typeof data.new_score === 'number' ? data.new_score.toFixed(1) : '-';
      alert(`${data.message}\n\nScore: ${oldScore} → ${newScore}`);
      fetchData();
    } catch (err) {
      alert('Fout: ' + err.message);
    } finally {
      setRematchingIds(prev => prev.filter(id => id !== matchId));
    }
  };
  
  // Simulated company handlers
  const viewSimulatedCompanyDetail = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/simulated-company/${id}`);
      const data = await res.json();
      setModalType('simulated-company');
      setModalData(data);
      setModalOpen(true);
    } catch (err) {
      alert('Kon details niet laden');
    }
  };
  
  const runSimulatedMatching = async (id, name) => {
    if (!window.confirm(`Wil je matching uitvoeren voor "${name}"?\n\nDit matcht het bedrijf met alle COMPLETED kandidaten (zonder notificaties).`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/simulated-company/${id}/match`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Matching mislukt');
      alert(`${data.message}\n\nKandidaten verwerkt: ${data.candidates_processed}\nMatches gecreëerd: ${data.matches_created}`);
      fetchData();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };
  
  const viewSimulatedMatches = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/api/admin/simulated-company/${id}/matches`);
      const data = await res.json();
      setModalType('simulated-matches');
      setModalData({ id, matches: data.matches || [] });
      setModalOpen(true);
    } catch (err) {
      alert('Kon matches niet laden');
    }
  };
  
  const deleteSimulatedCompany = async (id, name) => {
    if (!window.confirm(`Weet je zeker dat je gesimuleerd bedrijf "${name}" wilt verwijderen?\n\nDit verwijdert ook alle gerelateerde matches!`)) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/simulated-company/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verwijderen mislukt');
      alert(data.message || 'Gesimuleerd bedrijf verwijderd');
      fetchData();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };
  
  const exportScorecard = async (id, name, format = 'pdf') => {
    setExportingScorecard(id);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/export/simulated-company/${id}/scorecard?format=${format}`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Export mislukt');
      const blob = await res.blob();
      const slug = (name || id).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `match-scorecard-${slug}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();
    } catch (err) {
      alert('Export mislukt: ' + err.message);
    } finally {
      setExportingScorecard(null);
    }
  };
  
  // Import handlers
  const openImportModal = (type) => {
    setImportType(type);
    setImportJson('');
    setImportModalOpen(true);
  };
  
  const handleImport = async () => {
    if (!importJson.trim()) {
      alert('Voer JSON data in');
      return;
    }
    
    let profileData;
    try {
      profileData = JSON.parse(importJson);
    } catch (e) {
      alert('Ongeldige JSON: ' + e.message);
      return;
    }
    
    setImportLoading(true);
    try {
      const endpoint = importType === 'candidate' 
        ? '/api/admin/import/candidate' 
        : '/api/admin/import/company';
      
      const res = await authFetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_data: profileData })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import mislukt');
      
      alert(data.message || 'Import succesvol');
      setImportModalOpen(false);
      setImportJson('');
      fetchData();
    } catch (err) {
      alert('Import fout: ' + err.message);
    } finally {
      setImportLoading(false);
    }
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImportJson(event.target.result);
    };
    reader.readAsText(file);
  };
  
  // Status badge helper
  const getStatusBadge = (status) => {
    const map = {
      'COMPLETED': 'success',
      'ONBOARDING': 'warning',
      'MATCHING': 'info',
    };
    return <Badge variant={map[status] || 'default'}>{status}</Badge>;
  };
  
  // Show login screen if not authenticated
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Authenticatie controleren...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Laden...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="p-6 text-center">
          <div className="text-red-500 mb-4">Fout: {error}</div>
          <Button onClick={fetchData}>Opnieuw proberen</Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Betaaldsolliciteren.nl - Beheer & Debugging</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => openImportModal('candidate')} variant="success" size="sm">
                📥 Import Kandidaat
              </Button>
              <Button onClick={() => openImportModal('company')} variant="success" size="sm">
                📥 Import Bedrijf
              </Button>
              <Button onClick={triggerMatching} variant="secondary" size="sm">
                ▶️ Run Matching
              </Button>
              <Button onClick={triggerNotifications} variant="secondary" size="sm">
                📧 Send Notifications
              </Button>
              <Button onClick={fetchData} variant="secondary" size="sm">
                🔄 Refresh
              </Button>
              <Button 
                onClick={() => downloadJSON('/api/admin/export/all', 'export.json')} 
                variant="primary" 
                size="sm"
              >
                📦 Export All
              </Button>
              <Button 
                onClick={handleLogout} 
                variant="danger" 
                size="sm"
              >
                🚪 Uitloggen
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Kandidaten" value={stats.total_candidates} subtitle={`${stats.candidates_completed} completed`} color="blue" />
            <StatCard title="Bedrijven" value={stats.total_companies} subtitle={`${stats.companies_completed} completed`} color="green" />
            <StatCard title="Matches" value={stats.total_matches} subtitle="totaal berekend" color="purple" />
            <StatCard title="Succesvolle Matches" value={stats.successful_matches} subtitle="score >= threshold" color="orange" />
            <StatCard title="Match Drempel" value={stats.match_threshold?.toFixed(2) || '-'} subtitle="dynamisch" color="red" />
          </div>
        )}
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-4">
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              📊 Overzicht
            </TabButton>
            <TabButton active={activeTab === 'candidates'} onClick={() => setActiveTab('candidates')}>
              👤 Kandidaten ({candidates.length})
            </TabButton>
            <TabButton active={activeTab === 'companies'} onClick={() => setActiveTab('companies')}>
              🏢 Bedrijven ({companies.length})
            </TabButton>
            <TabButton active={activeTab === 'matches'} onClick={() => setActiveTab('matches')}>
              🤝 Matches ({matches.length})
            </TabButton>
            <TabButton active={activeTab === 'simulated'} onClick={() => setActiveTab('simulated')}>
              🤖 Gesimuleerd ({simulatedCompanies.length})
            </TabButton>
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Candidates */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Recente Kandidaten</h3>
              <div className="space-y-2">
                {candidates.slice(0, 5).map(c => (
                  <div key={c.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.email}</div>
                    </div>
                    {getStatusBadge(c.status)}
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Recent Companies */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Recente Bedrijven</h3>
              <div className="space-y-2">
                {companies.slice(0, 5).map(c => (
                  <div key={c.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.sector || 'Geen sector'}</div>
                    </div>
                    {getStatusBadge(c.status)}
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Recent Matches */}
            <Card className="p-4 md:col-span-2">
              <h3 className="font-semibold mb-3">Recente Matches</h3>
              <Table headers={['Kandidaat', 'Bedrijf', 'Score', 'Match?', 'Datum']}>
                {matches.slice(0, 10).map(m => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{m.candidate_name}</td>
                    <td className="px-4 py-2 text-sm">{m.company_name}</td>
                    <td className="px-4 py-2 text-sm font-medium">{m.score?.toFixed(1)}</td>
                    <td className="px-4 py-2">
                      {m.is_match ? <Badge variant="success">✓ Match</Badge> : <Badge variant="default">Geen match</Badge>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString('nl-NL') : '-'}
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          </div>
        )}
        
        {activeTab === 'candidates' && (
          <Card>
            <Table headers={['ID', 'Naam', 'Email', 'Status', 'Matches', 'Acties']}>
              {candidates.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-500">{c.id}</td>
                  <td className="px-4 py-2 text-sm font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{c.email || '-'}</td>
                  <td className="px-4 py-2">{getStatusBadge(c.status)}</td>
                  <td className="px-4 py-2 text-sm">{c.match_count}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => viewCandidateDetail(c.id)}>
                        👁️ Details
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => downloadJSON(`/api/admin/export/candidate/${c.id}`, `${c.id}.json`)}>
                        ⬇️ JSON
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteCandidate(c.id, c.name)}>
                        🗑️ Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
        
        {activeTab === 'companies' && (
          <Card>
            <Table headers={['ID', 'Naam', 'Email', 'Sector', 'Status', 'Matches', 'Acties']}>
              {companies.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-500">{c.id}</td>
                  <td className="px-4 py-2 text-sm font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{c.email || '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{c.sector || '-'}</td>
                  <td className="px-4 py-2">{getStatusBadge(c.status)}</td>
                  <td className="px-4 py-2 text-sm">{c.match_count}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => viewCompanyDetail(c.id)}>
                        👁️ Details
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => downloadJSON(`/api/admin/export/company/${c.id}`, `${c.id}.json`)}>
                        ⬇️ JSON
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteCompany(c.id, c.name)}>
                        🗑️ Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
        
        {activeTab === 'matches' && (
          <Card>
            <Table headers={['ID', 'Kandidaat', 'Bedrijf', 'Score', 'Match?', 'Kandidaat-feedback', 'Datum', 'Acties']}>
              {matches.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-500">{m.id}</td>
                  <td className="px-4 py-2 text-sm">{m.candidate_name}</td>
                  <td className="px-4 py-2 text-sm">{m.company_name}</td>
                  <td className="px-4 py-2 text-sm font-bold">{m.score?.toFixed(1)}</td>
                  <td className="px-4 py-2">
                    {m.is_match ? <Badge variant="success">✓ Match</Badge> : <Badge variant="default">Geen match</Badge>}
                  </td>
                  <td className="px-4 py-2">
                    {m.candidate_feedback === 'rejected'
                      ? <Badge variant="error">👎 Afgewezen</Badge>
                      : m.candidate_feedback === 'liked'
                        ? <Badge variant="success">👍 Interesse</Badge>
                        : <span className="text-xs text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {m.created_at ? new Date(m.created_at).toLocaleString('nl-NL') : '-'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={rematchingIds.includes(m.id)}
                        onClick={() => rematchSingle(m.id, m.candidate_name, m.company_name)}
                      >
                        {rematchingIds.includes(m.id) ? '⏳ Rematch...' : '🔁 Rematch'}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => viewMatchDetail(m.id)}>
                        👁️ Details
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => downloadJSON(`/api/admin/export/match/${m.id}`, `match_${m.id}.json`)}>
                        ⬇️ JSON
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteMatch(m.id)}>
                        🗑️ Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
        
        {activeTab === 'simulated' && (
          <Card>
            <div className="p-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2 text-blue-800">
                <span className="text-lg">🤖</span>
                <span className="font-medium">Gesimuleerde Bedrijven voor Sales Outreach</span>
              </div>
              <p className="text-sm text-blue-600 mt-1">
                Deze bedrijven zijn automatisch gegenereerd via Google Search. Gebruik de "Match" knop om kandidaten te matchen (zonder notificaties).
              </p>
            </div>
            <Table headers={['ID', 'Naam', 'Sector', 'Locatie', 'Matches', 'Laatst Gematcht', 'Acties']}>
              {simulatedCompanies.map(sc => (
                <tr key={sc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-500">{sc.id}</td>
                  <td className="px-4 py-2 text-sm font-medium">{sc.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{sc.sector || '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{sc.location || '-'}</td>
                  <td className="px-4 py-2 text-sm">
                    <Badge variant={sc.match_count > 0 ? 'success' : 'default'}>
                      {sc.match_count} matches
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {sc.last_matched_at ? new Date(sc.last_matched_at).toLocaleString('nl-NL') : 'Nooit'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="success" onClick={() => runSimulatedMatching(sc.id, sc.name)}>
                        ▶️ Match
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => viewSimulatedCompanyDetail(sc.id)}>
                        👁️ Profiel
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => viewSimulatedMatches(sc.id)}>
                        🤝 Matches
                      </Button>
                      <Button size="sm" variant="primary" disabled={exportingScorecard === sc.id} onClick={() => exportScorecard(sc.id, sc.name, 'pdf')}>
                        {exportingScorecard === sc.id ? '⏳ Bezig…' : '📄 Scorecard'}
                      </Button>
                      {!IS_PRODUCTION && (
                        <Button size="sm" variant="secondary" disabled={exportingScorecard === sc.id} onClick={() => exportScorecard(sc.id, sc.name, 'html')}>
                          🌐 HTML
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => deleteSimulatedCompany(sc.id, sc.name)}>
                        🗑️
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
            {simulatedCompanies.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">🤖</div>
                <p>Nog geen gesimuleerde bedrijven.</p>
                <p className="text-sm mt-1">Draai het simulatiescript om bedrijven toe te voegen.</p>
              </div>
            )}
          </Card>
        )}
      </main>
      
      {/* Detail Modal */}
      <Modal 
        isOpen={modalOpen} 
        onClose={() => { setModalOpen(false); setEditMode(false); }}
        title={
          modalType === 'candidate' ? `Kandidaat: ${modalData?.id}` :
          modalType === 'company' ? `Bedrijf: ${modalData?.id}` :
          modalType === 'match' ? `Match #${modalData?.id}` :
          modalType === 'simulated-company' ? `🤖 Gesimuleerd: ${modalData?.source_name || modalData?.id}` :
          modalType === 'simulated-matches' ? `🤝 Matches voor: ${modalData?.id}` : 'Details'
        }
      >
        {modalData && (
          <div className="space-y-4">
            {/* Simulated Matches View */}
            {modalType === 'simulated-matches' && (
              <div className="space-y-3">
                {modalData.matches && modalData.matches.length > 0 ? (
                  modalData.matches.map((m, i) => (
                    <div key={i} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{m.candidate_name}</span>
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                          Score: {m.score?.toFixed(1)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">{m.candidate_email}</div>
                      {m.match_details && m.match_details.levels && (
                        <div className="grid grid-cols-4 gap-2 text-xs mt-3">
                          <div className="bg-blue-50 p-2 rounded">
                            <div className="font-medium text-blue-700">Identity</div>
                            <div className="text-blue-900 font-bold">{m.match_details.levels.identity?.score?.toFixed(1)}</div>
                          </div>
                          <div className="bg-purple-50 p-2 rounded">
                            <div className="font-medium text-purple-700">Ambition</div>
                            <div className="text-purple-900 font-bold">{m.match_details.levels.ambition?.score?.toFixed(1)}</div>
                          </div>
                          <div className="bg-orange-50 p-2 rounded">
                            <div className="font-medium text-orange-700">Skills</div>
                            <div className="text-orange-900 font-bold">{m.match_details.levels.skills?.score?.toFixed(1)}</div>
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <div className="font-medium text-green-700">Constraints</div>
                            <div className="text-green-900 font-bold">{m.match_details.levels.constraints?.score?.toFixed(1)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">🔍</div>
                    <p>Nog geen matches. Klik op "Match" om matching uit te voeren.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Regular detail views */}
            {modalType !== 'simulated-matches' && (
              <>
                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                  <div>
                    <div className="text-xs text-gray-500">ID</div>
                    <div className="font-mono text-sm">{modalData.id}</div>
                  </div>
                  {modalData.status && (
                    <div>
                      <div className="text-xs text-gray-500">Status</div>
                      <div>{getStatusBadge(modalData.status)}</div>
                    </div>
                  )}
                  {modalData.score !== undefined && (
                    <div>
                      <div className="text-xs text-gray-500">Score</div>
                      <div className="font-bold text-lg">{modalData.score?.toFixed(1)}</div>
                    </div>
                  )}
                  {modalData.created_at && (
                    <div>
                      <div className="text-xs text-gray-500">Aangemaakt</div>
                      <div className="text-sm">{new Date(modalData.created_at).toLocaleString('nl-NL')}</div>
                    </div>
                  )}
                  {modalData.source_sector && (
                    <div>
                      <div className="text-xs text-gray-500">Sector</div>
                      <div className="text-sm">{modalData.source_sector}</div>
                    </div>
                  )}
                  {modalData.source_location && (
                    <div>
                      <div className="text-xs text-gray-500">Locatie</div>
                      <div className="text-sm">{modalData.source_location}</div>
                    </div>
                  )}
                </div>
                
                {/* Profile data / Match details */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">
                      {modalType === 'match' ? 'Match Details' : 'Profile Data'}
                    </h4>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => {
                          const data = modalType === 'match' ? modalData.match_details : modalData.profile_data;
                          navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                          alert('Gekopieerd naar clipboard!');
                        }}
                      >
                        📋 Copy
                      </Button>
                      {modalType !== 'simulated-company' && (
                        <Button 
                          size="sm" 
                          variant={editMode ? 'secondary' : 'primary'}
                          onClick={() => {
                            if (!editMode) {
                              const data = modalType === 'match' ? modalData.match_details : modalData.profile_data;
                              setEditJson(JSON.stringify(data, null, 2));
                            }
                            setEditMode(!editMode);
                          }}
                        >
                          {editMode ? '❌ Annuleer' : '✏️ Bewerken'}
                        </Button>
                      )}
                      {editMode && (
                        <Button 
                          size="sm" 
                          variant="success"
                          disabled={editSaving}
                          onClick={async () => {
                            try {
                              const parsed = JSON.parse(editJson);
                              setEditSaving(true);
                              const endpoint = modalType === 'match' 
                                ? `${API_BASE}/api/admin/match/${modalData.id}`
                                : `${API_BASE}/api/admin/${modalType}/${modalData.id}`;
                              const body = modalType === 'match' 
                                ? { match_details: parsed }
                                : { profile_data: parsed };
                              const res = await authFetch(endpoint, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(body)
                              });
                              if (!res.ok) throw new Error('Opslaan mislukt');
                              alert('Opgeslagen!');
                              setEditMode(false);
                              setModalOpen(false);
                              fetchData();
                            } catch (err) {
                              alert('Fout: ' + err.message);
                            } finally {
                              setEditSaving(false);
                            }
                          }}
                        >
                          💾 Opslaan
                        </Button>
                  )}
                </div>
              </div>
              {editMode ? (
                <textarea
                  className="w-full h-96 bg-gray-900 text-green-400 p-4 rounded font-mono text-xs"
                  value={editJson}
                  onChange={(e) => setEditJson(e.target.value)}
                />
              ) : (
                <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-auto max-h-96 text-xs">
                  {JSON.stringify(
                    modalType === 'match' ? modalData.match_details : modalData.profile_data, 
                    null, 
                    2
                  )}
                </pre>
              )}
            </div>

            {modalType === 'simulated-company' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Gevonden Matches JSON</h4>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(modalData.matches || [], null, 2));
                      alert('Matches JSON gekopieerd naar clipboard!');
                    }}
                  >
                    📋 Copy Matches
                  </Button>
                </div>
                <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-auto max-h-96 text-xs">
                  {JSON.stringify(modalData.matches || [], null, 2)}
                </pre>
              </div>
            )}
            
            {/* Matches list for candidate/company */}
            {modalData.matches && modalData.matches.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">Matches ({modalData.matches.length})</h4>
                <div className="space-y-1">
                  {modalData.matches.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span className="font-mono text-xs">{m.company_id || m.candidate_id}</span>
                      <span className="font-bold">{m.score?.toFixed(1)}</span>
                      {m.is_match ? <Badge variant="success">Match</Badge> : <Badge>Geen match</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}
      </Modal>
      
      {/* Import Modal */}
      <Modal 
        isOpen={importModalOpen} 
        onClose={() => setImportModalOpen(false)}
        title={`Importeer ${importType === 'candidate' ? 'Kandidaat' : 'Bedrijf'}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Plak de profile_data JSON of upload een JSON bestand dat je eerder hebt geëxporteerd.
          </p>
          
          {/* File upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload JSON bestand
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          
          {/* JSON textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Of plak JSON direct
            </label>
            <textarea
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{"candidate_id": "cand-xxx", "personalia": {"name": "..."}}'
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setImportModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              variant="success" 
              onClick={handleImport}
              disabled={importLoading || !importJson.trim()}
            >
              {importLoading ? 'Importeren...' : `Importeer ${importType === 'candidate' ? 'Kandidaat' : 'Bedrijf'}`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
